import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { format } from "date-fns";
import { toast } from "sonner";
import {
  Star,
  MapPin,
  WifiHigh,
  ForkKnife,
  Snowflake,
  SwimmingPool,
  Car,
  Barbell,
} from "@phosphor-icons/react";
import { Button } from "../components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { Calendar } from "../components/ui/calendar";
import { Textarea } from "../components/ui/textarea";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const AMENITY_ICONS = {
  "Free WiFi": WifiHigh,
  Restaurant: ForkKnife,
  AC: Snowflake,
  Pool: SwimmingPool,
  Parking: Car,
  Gym: Barbell,
};

export default function HotelDetailPage() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [hotel, setHotel] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [checkIn, setCheckIn] = useState(new Date());
  const [checkOut, setCheckOut] = useState(new Date(Date.now() + 86400000));
  const [guests, setGuests] = useState(2);
  const [rooms, setRooms] = useState(1);
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState("");
  const [submittingReview, setSubmittingReview] = useState(false);
  const [booking, setBooking] = useState(false);

  useEffect(() => {
    api.get(`/hotels/${id}`).then(({ data }) => setHotel(data));
    api.get(`/reviews/hotel/${id}`).then(({ data }) => setReviews(data));
  }, [id]);

  if (!hotel)
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="w-10 h-10 border-2 border-gold border-t-transparent rounded-full animate-spin" />
      </div>
    );

  const nights = Math.max(
    1,
    Math.round((checkOut - checkIn) / (1000 * 60 * 60 * 24))
  );
  const subtotal = nights * rooms * hotel.price_per_night;
  const total = subtotal;

  const handleBook = async () => {
    if (!user) {
      toast.error("Please sign in to book");
      nav("/login");
      return;
    }
    setBooking(true);
    try {
      const { data } = await api.post("/bookings", {
        hotel_id: hotel.id,
        check_in: checkIn.toISOString(),
        check_out: checkOut.toISOString(),
        guests,
        rooms,
      });
      toast.success("Booking created — proceeding to payment");
      nav(`/checkout/${data.id}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Booking failed");
    } finally {
      setBooking(false);
    }
  };

  const submitReview = async () => {
    if (!user) {
      toast.error("Please sign in to review");
      return;
    }
    setSubmittingReview(true);
    try {
      await api.post("/reviews", { hotel_id: hotel.id, rating, comment });
      const { data } = await api.get(`/reviews/hotel/${hotel.id}`);
      setReviews(data);
      const { data: h } = await api.get(`/hotels/${hotel.id}`);
      setHotel(h);
      setComment("");
      toast.success("Review posted");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    } finally {
      setSubmittingReview(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10">
      {/* Bento gallery */}
      <div className="grid grid-cols-12 grid-rows-2 gap-3 h-[60vh] mb-10 rounded-2xl overflow-hidden">
        <img
          src={hotel.images[0]}
          alt=""
          className="col-span-12 md:col-span-8 row-span-2 w-full h-full object-cover"
          data-testid="hotel-hero-img"
        />
        {hotel.images.slice(1, 3).map((src, i) => (
          <img
            key={i}
            src={src}
            alt=""
            className="hidden md:block col-span-4 row-span-1 w-full h-full object-cover"
          />
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
        <div className="lg:col-span-2">
          <p className="text-xs tracking-[0.3em] uppercase text-gold mb-3">
            {hotel.property_type}
          </p>
          <h1 className="font-serif text-4xl lg:text-5xl text-midnight mb-4" data-testid="hotel-name">
            {hotel.name}
          </h1>
          <div className="flex items-center gap-6 mb-8 text-sm">
            <div className="flex items-center gap-1 text-neutral-600">
              <MapPin size={16} weight="duotone" className="text-gold" />
              {hotel.address}
            </div>
            <div className="flex items-center gap-1">
              <Star size={16} weight="fill" className="text-gold" />
              <span className="text-midnight font-medium">{hotel.rating}</span>
              <span className="text-neutral-500">({reviews.length} reviews)</span>
            </div>
          </div>

          <p className="text-neutral-700 leading-relaxed mb-10">{hotel.description}</p>

          <h3 className="font-serif text-2xl mb-4 text-midnight">Amenities</h3>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-12">
            {hotel.amenities.map((a) => {
              const Icon = AMENITY_ICONS[a] || Star;
              return (
                <div key={a} className="flex items-center gap-3 text-neutral-700">
                  <Icon size={20} weight="duotone" className="text-gold" />
                  <span className="text-sm">{a}</span>
                </div>
              );
            })}
          </div>

          {/* Reviews */}
          <h3 className="font-serif text-2xl mb-6 text-midnight">Reviews</h3>
          {user && (
            <div className="bg-white border border-black/5 rounded-2xl p-6 mb-8" data-testid="review-form">
              <p className="text-xs tracking-widest uppercase text-neutral-500 mb-3">Your rating</p>
              <div className="flex gap-1 mb-4">
                {[1, 2, 3, 4, 5].map((n) => (
                  <button
                    key={n}
                    onClick={() => setRating(n)}
                    data-testid={`star-${n}`}
                    aria-label={`${n} star`}
                  >
                    <Star
                      size={26}
                      weight={n <= rating ? "fill" : "regular"}
                      className={n <= rating ? "text-gold" : "text-neutral-300"}
                    />
                  </button>
                ))}
              </div>
              <Textarea
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder="Share your experience..."
                className="mb-4"
                data-testid="review-textarea"
              />
              <Button
                onClick={submitReview}
                disabled={submittingReview || !comment.trim()}
                className="bg-midnight hover:bg-midnight-hover text-bone"
                data-testid="submit-review-btn"
              >
                {submittingReview ? "Posting..." : "Post Review"}
              </Button>
            </div>
          )}

          <div className="space-y-6">
            {reviews.length === 0 ? (
              <p className="text-neutral-500 text-sm">Be the first to review.</p>
            ) : (
              reviews.map((r) => (
                <div key={r.id} className="border-b border-black/5 pb-6" data-testid={`review-${r.id}`}>
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-9 h-9 rounded-full bg-gold/20 flex items-center justify-center font-serif text-midnight">
                      {r.user_name?.[0]?.toUpperCase() || "G"}
                    </div>
                    <div>
                      <p className="text-sm font-medium text-midnight">{r.user_name}</p>
                      <div className="flex items-center gap-1">
                        {[...Array(5)].map((_, i) => (
                          <Star
                            key={i}
                            size={12}
                            weight={i < r.rating ? "fill" : "regular"}
                            className={i < r.rating ? "text-gold" : "text-neutral-300"}
                          />
                        ))}
                      </div>
                    </div>
                  </div>
                  <p className="text-neutral-700 text-sm leading-relaxed">{r.comment}</p>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Booking widget */}
        <aside>
          <div className="sticky top-28 bg-white border border-black/10 rounded-2xl p-6 shadow-lg" data-testid="booking-widget">
            <div className="flex items-baseline gap-2 mb-1">
              <span className="font-serif text-3xl text-midnight">
                ₹{hotel.price_per_night.toLocaleString("en-IN")}
              </span>
              <span className="text-sm text-neutral-500">/ night</span>
            </div>
            <div className="my-6 grid grid-cols-2 gap-3">
              <Popover>
                <PopoverTrigger asChild>
                  <button className="text-left border border-black/10 rounded-xl px-3 py-2 hover:bg-stone" data-testid="detail-checkin-btn">
                    <p className="text-[10px] uppercase tracking-widest text-neutral-500">Check-in</p>
                    <p className="text-sm text-midnight">{format(checkIn, "dd MMM")}</p>
                  </button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0">
                  <Calendar
                    mode="single"
                    selected={checkIn}
                    onSelect={(d) => d && setCheckIn(d)}
                    disabled={(d) => d < new Date(new Date().toDateString())}
                  />
                </PopoverContent>
              </Popover>
              <Popover>
                <PopoverTrigger asChild>
                  <button className="text-left border border-black/10 rounded-xl px-3 py-2 hover:bg-stone" data-testid="detail-checkout-btn">
                    <p className="text-[10px] uppercase tracking-widest text-neutral-500">Check-out</p>
                    <p className="text-sm text-midnight">{format(checkOut, "dd MMM")}</p>
                  </button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0">
                  <Calendar
                    mode="single"
                    selected={checkOut}
                    onSelect={(d) => d && setCheckOut(d)}
                    disabled={(d) => d <= checkIn}
                  />
                </PopoverContent>
              </Popover>
            </div>

            <div className="grid grid-cols-2 gap-3 mb-6">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="text-left border border-black/10 rounded-xl px-3 py-2 hover:bg-stone" data-testid="detail-guests-btn">
                    <p className="text-[10px] uppercase tracking-widest text-neutral-500">Guests</p>
                    <p className="text-sm text-midnight">{guests}</p>
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent>
                  {[1, 2, 3, 4, 5, 6].map((n) => (
                    <DropdownMenuItem key={n} onClick={() => setGuests(n)}>{n}</DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="text-left border border-black/10 rounded-xl px-3 py-2 hover:bg-stone" data-testid="detail-rooms-btn">
                    <p className="text-[10px] uppercase tracking-widest text-neutral-500">Rooms</p>
                    <p className="text-sm text-midnight">{rooms}</p>
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent>
                  {[1, 2, 3, 4].map((n) => (
                    <DropdownMenuItem key={n} onClick={() => setRooms(n)}>{n}</DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>

            <div className="space-y-2 pb-4 mb-4 border-b border-black/10 text-sm">
              <div className="flex justify-between text-neutral-600">
                <span>₹{hotel.price_per_night.toLocaleString("en-IN")} × {nights} nights × {rooms} rooms</span>
                <span className="text-midnight">₹{subtotal.toLocaleString("en-IN")}</span>
              </div>
              <div className="flex justify-between text-neutral-600">
                <span>Taxes & fees</span>
                <span className="text-emerald-700">Included</span>
              </div>
            </div>
            <div className="flex justify-between mb-6">
              <span className="font-serif text-lg text-midnight">Total</span>
              <span className="font-serif text-2xl text-midnight" data-testid="booking-total">
                ₹{total.toLocaleString("en-IN")}
              </span>
            </div>

            <Button
              onClick={handleBook}
              disabled={booking}
              className="w-full bg-midnight hover:bg-midnight-hover text-bone py-6 text-base tracking-wide"
              data-testid="reserve-btn"
            >
              {booking ? "Creating booking..." : "Reserve Now"}
            </Button>
            <p className="text-[11px] text-center text-neutral-500 mt-3 tracking-wider">
              You won't be charged yet
            </p>
          </div>
        </aside>
      </div>
    </div>
  );
}
